import { Component, For, Show, createSignal, createMemo } from 'solid-js';
import { TbChevronUp, TbChevronDown, TbSearch } from 'solid-icons/tb';

export interface Column<T> {
  key: string;
  label: string;
  sortable?: boolean;
  render?: (item: T) => any;
}

interface DataTableProps<T> {
  data: T[];
  columns: Column<T>[];
  loading?: boolean;
  onRowClick?: (item: T) => void;
  searchable?: boolean;
  searchPlaceholder?: string;
  defaultSortKey?: string;
  defaultSortOrder?: 'asc' | 'desc';
  getRowClass?: (item: T) => string;
  rowPadding?: string;
}

/**
 * DataTable Component
 * Generic table with sorting, searching, and custom rendering
 */
function DataTable<T extends Record<string, any>>(props: DataTableProps<T>) {
  const [sortKey, setSortKey] = createSignal<string>(props.defaultSortKey || '');
  const [sortOrder, setSortOrder] = createSignal<'asc' | 'desc'>(props.defaultSortOrder || 'asc');
  const [searchQuery, setSearchQuery] = createSignal('');

  // Handle column sort
  const handleSort = (key: string) => {
    if (sortKey() === key) {
      setSortOrder(sortOrder() === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortOrder('asc');
    }
  };

  // Filter and sort data
  const processedData = createMemo(() => {
    let result = [...props.data];

    // Search filter (case-insensitive for all fields, supports comma-separated OR filter)
    if (searchQuery() && props.searchable) {
      const query = searchQuery().toLowerCase().trim();
      // Split by comma and filter empty terms - supports "term1,term2,term3" format
      const searchTerms = query.split(',').map(term => term.trim()).filter(Boolean);

      if (searchTerms.length > 0) {
        // Filter: each row must match at least ONE search term (OR logic)
        result = result.filter((item) =>
          searchTerms.some(term =>
            props.columns.some((col) => {
              const value = item[col.key];
              return String(value).toLowerCase().includes(term);
            })
          )
        );
      }
    }

    // Sort
    if (sortKey()) {
      result.sort((a, b) => {
        // Handle null/undefined items
        if (!a || !b) return 0;

        let aVal = a[sortKey()];
        let bVal = b[sortKey()];

        // Handle null/undefined values
        if (aVal == null && bVal == null) return 0;
        if (aVal == null) return 1;
        if (bVal == null) return -1;

        // Convert dates to timestamps for proper comparison
        if (aVal instanceof Date) aVal = aVal.getTime();
        if (bVal instanceof Date) bVal = bVal.getTime();

        // Handle ISO date strings
        if (typeof aVal === 'string' && aVal.match(/^\d{4}-\d{2}-\d{2}/)) {
          aVal = new Date(aVal).getTime();
        }
        if (typeof bVal === 'string' && bVal.match(/^\d{4}-\d{2}-\d{2}/)) {
          bVal = new Date(bVal).getTime();
        }

        if (aVal === bVal) return 0;

        const comparison = aVal < bVal ? -1 : 1;
        return sortOrder() === 'asc' ? comparison : -comparison;
      });
    }

    return result;
  });

  return (
    <div class="rounded-lg shadow-card overflow-hidden">
      {/* Search Bar */}
      <Show when={props.searchable}>
        <div class="p-4 md:p-6 border-b border-dashed border-gray-200 dark:border-gray-700">
          <div class="relative">
            <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <TbSearch size={20} class="text-gray-400" />
            </div>
            <input
              type="text"
              class="block w-full pl-10 pr-3 py-2 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-light-dark text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-brand focus:border-brand font-body"
              placeholder={props.searchPlaceholder || 'Search...'}
              value={searchQuery()}
              onInput={(e) => setSearchQuery(e.currentTarget.value)}
            />
          </div>
        </div>
      </Show>

      {/* Table */}
      <div class="w-full overflow-x-auto table-scrollbar">
        <table class="w-full divide-y divide-dashed divide-gray-200 dark:divide-gray-700 transaction-table">
          <thead class="border-b border-dashed border-gray-200 dark:border-gray-700">
            <tr class="table-row">
              <For each={props.columns}>
                {(column) => (
                  <th
                    class={`px-2 md:px-4 py-2 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider letter-space-inherit font-body table-cell min-w-fit ${
                      column.sortable ? 'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-900 transition-colors' : ''
                    }`}
                    onClick={() => column.sortable && handleSort(column.key)}
                  >
                    <div class="flex items-center gap-2">
                      {column.label}
                      <Show when={column.sortable}>
                        <div class="flex flex-col">
                          <TbChevronUp
                            size={12}
                            class={
                              sortKey() === column.key && sortOrder() === 'asc'
                                ? 'text-orange-500'
                                : 'text-gray-400'
                            }
                          />
                          <TbChevronDown
                            size={12}
                            class={
                              sortKey() === column.key && sortOrder() === 'desc'
                                ? 'text-orange-500'
                                : 'text-gray-400'
                            }
                          />
                        </div>
                      </Show>
                    </div>
                  </th>
                )}
              </For>
            </tr>
          </thead>
          <tbody class="divide-y divide-dashed divide-gray-200 dark:divide-gray-700">
            <Show
              when={!props.loading && processedData().length > 0}
              fallback={
                <Show
                  when={props.loading}
                  fallback={
                    <tr>
                      <td
                        colspan={props.columns.length}
                        class="px-6 py-8 text-center text-sm text-gray-500 dark:text-gray-400 font-body"
                      >
                        No data available
                      </td>
                    </tr>
                  }
                >
                  {/* Skeleton placeholder rows while loading - 9 rows for better visual feedback */}
                  <For each={Array(9).fill(null)}>
                    {() => (
                      <tr class="table-row">
                        <For each={props.columns}>
                          {() => (
                            <td class={`table-cell h-12 px-2 md:px-4 ${props.rowPadding || 'py-3'} whitespace-nowrap text-xs text-gray-900 dark:text-gray-100 font-body align-middle min-w-fit`}>
                              <div class="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
                            </td>
                          )}
                        </For>
                      </tr>
                    )}
                  </For>
                </Show>
              }
            >
              <For each={processedData()}>
                {(item, index) => (
                  <tr
                    class={`table-row uppercase transition-all ${
                      index() % 2 === 0
                        ? 'bg-gray-50 dark:bg-gray-900/30'
                        : 'bg-white dark:bg-gray-800/40'
                    } ${
                      props.onRowClick
                        ? 'cursor-pointer hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors'
                        : 'hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors'
                    } ${props.getRowClass?.(item) || ''}`}
                    onClick={() => props.onRowClick?.(item)}
                  >
                    <For each={props.columns}>
                      {(column) => (
                        <td class={`table-cell h-12 px-2 md:px-4 ${props.rowPadding || 'py-3'} tracking-[1px] whitespace-nowrap text-xs text-gray-900 dark:text-gray-100 font-body align-middle min-w-fit`}>
                          {column.render ? column.render(item) : item[column.key]}
                        </td>
                      )}
                    </For>
                  </tr>
                )}
              </For>
            </Show>
          </tbody>
        </table>
      </div>

      {/* Footer with count */}
      <div class="px-6 py-4 border-t border-dashed border-gray-200 dark:border-gray-700 rounded-bl-lg rounded-br-lg">
        <p class="text-sm text-gray-700 dark:text-gray-300 font-body">
          Showing <span class="font-medium">{processedData().length}</span> of{' '}
          <span class="font-medium">{props.data.length}</span> results
        </p>
      </div>
    </div>
  );
}

export default DataTable;
